import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { PagedDto } from '../../../shared/dtos/paged.dto';
import { clamp } from '../util/number-utils';

type PagedQueryParams<T> = {
  queryKey: readonly unknown[];
  fetchPage: (page: number, pageSize: number) => Promise<PagedDto<T>>;
  pageSize: number;
  enabled?: boolean;
};

export type PagedQueryResult<T> = {
  items: T[];
  page: number;
  pageCount: number;
  pageSize: number;
  totalCount: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  isFetching: boolean;
};

type PageState = {
  criteria: string;
  page: number;
};

export function usePagedQuery<T>({
  queryKey,
  fetchPage,
  pageSize,
  enabled = true,
}: PagedQueryParams<T>): PagedQueryResult<T> {
  const criteria: string = JSON.stringify(queryKey);
  const [pageState, setPageState] = useState<PageState>({ criteria, page: 0 });

  const page: number = pageState.criteria === criteria ? pageState.page : 0;

  const query: UseQueryResult<PagedDto<T>> = useQuery({
    queryKey: [...queryKey, page],
    queryFn: async (): Promise<PagedDto<T>> => {
      try {
        return await fetchPage(page, pageSize);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    placeholderData: keepPreviousData,
    enabled,
  });

  const totalCount: number = query.data?.totalCount ?? 0;
  const pageCount: number = Math.max(Math.ceil(totalCount / pageSize), 1);

  if (query.data && !query.isPlaceholderData && page > pageCount - 1) {
    setPageState({ criteria, page: pageCount - 1 });
  }

  const setPage = (nextPage: number): void => {
    setPageState({ criteria, page: clamp(nextPage, 0, pageCount - 1) });
  };

  return {
    items: query.data?.items ?? [],
    page,
    pageCount,
    pageSize,
    totalCount,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
